import styled from "styled-components"
import Color from "../../constants/Color"
import Spacer from "../atoms/Spacer"
import * as Animation from "../../functionality/Animation"
import _Button from "../atoms/Button"
import Coffee from "../molecules/Coffee"
import { useRef } from "react"
import CascadeText from "../molecules/CascadeText"
import * as Links from "../../constants/Links"

type Props = {
    widthInPx: number
    heightInPx: number
    screenHeightInPx: number
}

const headlineSize: Animation.VariableValue = {
    end: 70,
    start: 30,
    endControl: 1600,   // Width in px
    startControl: 400,    // Width in px
}

const titleSize: Animation.VariableValue = {
    end: 30,
    start: 14,
    endControl: 1200,   // Width in px
    startControl: 600,    // Width in px
}

const spacerHeight: Animation.VariableValue = {
    end: 30,
    start: 10,
    endControl: 1400,   // Width in px
    startControl: 600,    // Width in px
}

const buttonFontSize: Animation.VariableValue = {
    end: 20,
    start: 14,
    endControl: 800,    // Width in px
    startControl: 600,    // Width in px
}

const headlineSingleLineTweenValues: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.3,      // Fraction up screen
    endControl: 0.6,        // Fraction up screen
}

const headlineTopLineTweenValues: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.2,      // Fraction up screen
    endControl: 0.4,        // Fraction up screen
}

const headlineBottomLineTweenValues: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.4,      // Fraction up screen
    endControl: 0.6,        // Fraction up screen
}

const textLineOneOpacityValues: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.6,      // Fraction up screen
    endControl: 0.75,        // Fraction up screen
}

const textLineTwoOpacityValues: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.65,      // Fraction up screen
    endControl: 0.8,        // Fraction up screen
}

const buttonOpacityValues: Animation.VariableValue = {
    start: 0,
    end: 1,
    startControl: 0.8,      // Fraction up screen
    endControl: 0.9,        // Fraction up screen
}

const CTA: React.FC<Props> = ({ widthInPx, heightInPx, screenHeightInPx }) => {
    
    const ref = useRef<HTMLDivElement>(null)

    const headlineFontSizeInPx = Animation.calculateValue(headlineSize, widthInPx)
    const titleFontSizeInPx = Animation.calculateValue(titleSize, widthInPx)
    const singleSpacerHeightInPx = Animation.calculateValue(spacerHeight, widthInPx)
    const buttonFontSizeInPx = Animation.calculateValue(buttonFontSize, widthInPx)

    const headlineIsOneLine = (widthInPx > 900)
    const headlineHeight = headlineIsOneLine ? headlineFontSizeInPx : (headlineFontSizeInPx * 2.4)

    const yInPx = ref.current?.getBoundingClientRect().y ?? screenHeightInPx
    const fractionOnScreenValues: Animation.VariableValue = {
        start: 1,
        end: 0,
        startControl: (screenHeightInPx - heightInPx),
        endControl: screenHeightInPx,
    }
    const fractionOnScreen = Animation.calculateValue(fractionOnScreenValues, yInPx)
    const headlineSingleLineTween = Animation.calculateValue(headlineSingleLineTweenValues, fractionOnScreen)
    const headlineTopLineTween = Animation.calculateValue(headlineTopLineTweenValues, fractionOnScreen)
    const headlineBottomLineTween = Animation.calculateValue(headlineBottomLineTweenValues, fractionOnScreen)

    const textLineOneOpacity = Animation.calculateValue(textLineOneOpacityValues, fractionOnScreen)
    const textLineTwoOpacity = Animation.calculateValue(textLineTwoOpacityValues, fractionOnScreen)
    const buttonOpacity = Animation.calculateValue(buttonOpacityValues, fractionOnScreen)

    // console.log(fractionOnScreen)

    return <Wrapper ref={ref} widthInPx={widthInPx} heightInPx={heightInPx}>
        <Coffee left="0" bottom="0" widthInPx={widthInPx} saturation={0.3}/>
        <TextContainer rightPaddingInPx={singleSpacerHeightInPx * 2}>
            <Spacer heightInPx={singleSpacerHeightInPx}/>
            <Headline viewBox={`0 0 ${widthInPx} ${headlineHeight}`} heightInPx={headlineHeight}>
                {headlineIsOneLine ? <CascadeText text="It all starts with a conversation..."
                                                  fadeDuration={0.2}
                                                  tween={headlineSingleLineTween}
                                                  fill={Color.White}
                                                  fontSize={headlineFontSizeInPx}
                                                  textAnchor="end"
                                                  x={widthInPx}
                                                  y={headlineFontSizeInPx}/>
                                : <>
                                        <CascadeText text="It all starts with"
                                                     fadeDuration={0.2}
                                                     tween={headlineTopLineTween}
                                                     fill={Color.White}
                                                     fontSize={headlineFontSizeInPx}
                                                     textAnchor="end"
                                                     x={widthInPx}
                                                     y={headlineFontSizeInPx}/>
                                        <CascadeText text="a conversation"
                                                     fadeDuration={0.2}
                                                     tween={headlineBottomLineTween}
                                                     fill={Color.White}
                                                     fontSize={headlineFontSizeInPx}
                                                     textAnchor="end"
                                                     x={widthInPx}
                                                     y={headlineHeight}/>
                                </>}
            </Headline>
            <Spacer heightInPx={singleSpacerHeightInPx * 2}/>
            <Text fontSizeInPx={titleFontSizeInPx} opacity={textLineOneOpacity} lineHeight={1.5}>If you'd like to explore how we can help</Text>
            <Text fontSizeInPx={titleFontSizeInPx} opacity={textLineTwoOpacity} lineHeight={1.5}>you change the world</Text>
            <Spacer heightInPx={singleSpacerHeightInPx * 2}/>
            <Button fontSizeInPx={buttonFontSizeInPx} opacity={buttonOpacity} href={Links.MailTo}>Get in touch</Button>
        </TextContainer>
    </Wrapper>
}

export default CTA

const Wrapper = styled.div.attrs<{widthInPx: number, heightInPx: number}>(p => ({
    style: {
        width: p.widthInPx + "px",
        height: p.heightInPx + "px",
    }
}))`
    position: relative;
    background-color: ${Color.Black};
`

const TextContainer = styled.div.attrs<{rightPaddingInPx: number}>(p => ({
    style: {
        paddingRight: p.rightPaddingInPx + "px",
    }
}))`
    position: relative;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: end;

    z-index: 1;
`

const Headline = styled.svg.attrs<{heightInPx: number}>(p => ({
    style: {
        height: p.heightInPx + "px",
    }
}))`
    color: ${Color.White};
    font-family: "Mulish", sans-serif;
    font-weight: 100;
    letter-spacing: 0.05em;
    text-align: right;
`

const Text = styled.div.attrs<{fontSizeInPx: number, lineHeight: number, opacity: number}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        lineHeight: p.lineHeight,
        opacity: p.opacity,
    }
}))`
    color: ${Color.White};

    font-family: "Mulish", sans-serif;
    font-weight: 100;
    letter-spacing: 0.05em;
    text-align: right;
`

const Button = styled(_Button).attrs<{fontSizeInPx: number, opacity: number}>(p => ({
    style: {
        fontSize: p.fontSizeInPx + "px",
        opacity: p.opacity,
    }
}))``